import React, { memo, useState, useMemo, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import useSimulationStore from '../../../store/simulationStore';
import ChemicalFormula from '../ChemicalFormula';
import { getEffectiveColor } from '../colorHelpers';
import {
    unifiedSpeciesContainer,
    unifiedSpeciesItem,
    unifiedSpeciesLeft,
    unifiedSpeciesRight,
    unifiedSpeciesButton,
    unifiedSpeciesButtonFocused,
    unifiedControlSectionHeader,
    unifiedControlSectionIcon,
    unifiedControlSectionContent,
    unifiedControlGroup,
    unifiedControlLabel,
    unifiedControlButton,
    unifiedControlColorPicker,
    inheritanceIndicator,
    inheritanceArrow,
    customIndicator,
} from '../unifiedPanelStyles';

const UnifiedSpeciesPanel: React.FC = memo(() => {
    const {
        availableSpecies,
        speciesFocus,
        focusSpecies,
        unfocusSpecies,
        unfocusReaction,
        unfocusElement,
        reactionFocus,
        elementColors,
        speciesColors,
        reactionColors,
        speciesColorMode,
        setSpeciesColor,
        setElementColor,
        setReactionColor,
        setSpeciesColorMode,
        resetSpeciesToElementColors,
        clearAllFocus,
        activeChemistryConfig,
        availableReactions,
        updateActiveReactionInitialCount,
        resetSimulation
    } = useSimulationStore(
        state => ({
            availableSpecies: state.availableSpecies,
            speciesFocus: state.speciesFocus,
            focusSpecies: state.focusSpecies,
            unfocusSpecies: state.unfocusSpecies,
            unfocusReaction: state.unfocusReaction,
            unfocusElement: state.unfocusElement,
            reactionFocus: state.reactionFocus,
            elementColors: state.elementColors,
            speciesColors: state.speciesColors,
            reactionColors: state.reactionColors,
            speciesColorMode: state.speciesColorMode,
            setSpeciesColor: state.setSpeciesColor,
            setElementColor: state.setElementColor,
            setReactionColor: state.setReactionColor,
            setSpeciesColorMode: state.setSpeciesColorMode,
            resetSpeciesToElementColors: state.resetSpeciesToElementColors,
            clearAllFocus: state.clearAllFocus,
            activeChemistryConfig: state.activeChemistryConfig,
            availableReactions: state.availableReactions,
            updateActiveReactionInitialCount: state.updateActiveReactionInitialCount,
            resetSimulation: state.resetSimulation
        }),
        shallow
    );

    const [isExpanded, setIsExpanded] = useState(true);
    const [pendingReset, setPendingReset] = useState(false);

    // Calculate aggregate initial counts per species across all reactions
    const speciesInitialCounts = useMemo(() => {
        const counts: { [speciesId: string]: number } = {};
        activeChemistryConfig.reactions.forEach(reaction => {
            Object.entries(reaction.initialCounts).forEach(([speciesId, count]) => {
                counts[speciesId] = (counts[speciesId] || 0) + count;
            });
        });
        return counts;
    }, [activeChemistryConfig.reactions]);

    // Find which reaction contains a given species (for updating)
    const findReactionForSpecies = useCallback((speciesId: string) => {
        return activeChemistryConfig.reactions.find(reaction =>
            speciesId in reaction.initialCounts
        );
    }, [activeChemistryConfig.reactions]);

    // Handle count change - update store and mark pending reset
    const handleCountChange = useCallback((speciesId: string, newCount: number) => {
        const count = Math.max(0, Math.floor(newCount)); // Ensure non-negative integer
        const reaction = findReactionForSpecies(speciesId);
        if (reaction) {
            updateActiveReactionInitialCount(reaction.id, speciesId, count);
            setPendingReset(true);
        }
    }, [findReactionForSpecies, updateActiveReactionInitialCount]);

    // Apply changes by resetting simulation
    const handleApplyChanges = useCallback(() => {
        resetSimulation();
        setPendingReset(false);
    }, [resetSimulation]);

    return (
        <div style={unifiedSpeciesContainer}>
            <button
                style={unifiedControlSectionHeader}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span style={unifiedControlSectionIcon}>
                    {isExpanded ? '▼' : '▶'}
                </span>
                Species ({availableSpecies.length})
            </button>

            {isExpanded && (
                <div style={unifiedControlSectionContent}>
                    <div style={unifiedControlGroup}>
                        <label style={unifiedControlLabel}>
                            Set initial particle counts for each species
                        </label>
                    </div>

                    {availableSpecies.map(species => {
                        const focus = speciesFocus[species.id];
                        const focusLevel = focus?.focusLevel || 'none';
                        const isFocused = focusLevel === 'focus';
                        const colorInfo = getEffectiveColor(species.id, species.name, reactionColors, speciesColors, elementColors, activeChemistryConfig, availableReactions);
                        const currentColor = colorInfo.color;
                        const currentCount = speciesInitialCounts[species.id] || 0;

                        return (
                            <div key={species.id} style={unifiedSpeciesItem}>
                                <div style={unifiedSpeciesLeft}>
                                    <button
                                        style={{
                                            ...unifiedSpeciesButton,
                                            ...(isFocused ? unifiedSpeciesButtonFocused : {}),
                                            position: 'relative'
                                        }}
                                        onClick={() => {
                                            // Always deactivate higher level first
                                            Object.keys(reactionFocus).forEach(rid => {
                                                if (reactionFocus[rid]) {
                                                    unfocusReaction(rid);
                                                }
                                            });
                                            // Then toggle this species
                                            if (isFocused) {
                                                unfocusSpecies(species.id);
                                            } else {
                                                focusSpecies(species.id, 'direct');
                                            }
                                        }}
                                        title="Click to focus/unfocus this species"
                                    >
                                        <ChemicalFormula formula={species.name} />
                                        {focusLevel !== 'none' && (
                                            <span style={{
                                                position: 'absolute',
                                                top: '2px',
                                                right: '2px',
                                                fontSize: '8px',
                                                color: focusLevel === 'focus' ? '#4fc3f7' :
                                                    focusLevel === 'neutral' ? '#ffa726' :
                                                        focusLevel === 'dim' ? '#9e9e9e' : '#666'
                                            }}>
                                                {focusLevel === 'focus' ? '●' :
                                                    focusLevel === 'neutral' ? '○' :
                                                        focusLevel === 'dim' ? '◐' : ''}
                                            </span>
                                        )}
                                    </button>
                                </div>

                                <div style={unifiedSpeciesRight}>
                                    {/* Initial Count Input - uses onBlur to not fight typing */}
                                    <input
                                        type="number"
                                        min="0"
                                        step="10"
                                        key={`count-${species.id}-${currentCount}`}  // Force remount when store value changes
                                        defaultValue={currentCount}
                                        onBlur={(e) => handleCountChange(species.id, parseInt(e.target.value) || 0)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                (e.target as HTMLInputElement).blur();
                                            }
                                        }}
                                        style={countInputStyle}
                                        title={`Initial count for ${species.name} (press Enter or click away to apply)`}
                                    />

                                    <input
                                        type="color"
                                        value={currentColor}
                                        onChange={(e) => {
                                            const newColor = e.target.value;
                                            // Always clear higher levels
                                            Object.keys(reactionFocus).forEach(rid => {
                                                if (reactionFocus[rid]) {
                                                    unfocusReaction(rid);
                                                }
                                            });
                                            // Ensure this species remains active
                                            if (!isFocused) {
                                                focusSpecies(species.id, 'direct');
                                            }
                                            setSpeciesColor(species.id, newColor);

                                            // Smart override: Clear reaction colors that affect this species (higher level turns OFF)
                                            activeChemistryConfig.reactions.forEach(reaction => {
                                                const reactionDef = availableReactions.find(r => r.id === reaction.reactionDefId);
                                                if (reactionDef) {
                                                    const speciesInReaction = [
                                                        ...reactionDef.reactants.map(r => r.speciesId),
                                                        ...reactionDef.products.map(p => p.speciesId)
                                                    ];
                                                    if (speciesInReaction.includes(species.id) && reactionColors[reaction.id]) {
                                                        // Clear reaction color (higher level turns OFF)
                                                        setReactionColor(reaction.id, '');
                                                        unfocusReaction(reaction.id); // Turn OFF the reaction
                                                    }
                                                }
                                            });

                                            // Clear element colors for elements in this species so they inherit from species
                                            const elements = species.name.match(/[A-Z][a-z]?/g) || [];
                                            elements.forEach(element => {
                                                if (elementColors[element]) {
                                                    setElementColor(element, '');
                                                    unfocusElement(element); // Turn OFF the element
                                                }
                                            });
                                        }}
                                        style={unifiedControlColorPicker}
                                        title="Species color (overrides element color)"
                                    />

                                    <div
                                        style={inheritanceIndicator}
                                        title={colorInfo.inherited
                                            ? `Color inherited from ${colorInfo.source} level. Click to set custom color.`
                                            : `Custom color set directly. Click to change.`
                                        }
                                    >
                                        {colorInfo.inherited ? (
                                            <span style={inheritanceArrow}>↓</span>
                                        ) : (
                                            <span style={customIndicator}>●</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Apply Changes Button - shows when counts have changed */}
                    {pendingReset && (
                        <div style={unifiedControlGroup}>
                            <button
                                style={{ ...unifiedControlButton, backgroundColor: '#4CAF50', color: 'white' }}
                                onClick={handleApplyChanges}
                            >
                                ▶ Apply Changes & Reset
                            </button>
                        </div>
                    )}

                    <div style={unifiedControlGroup}>
                        <button
                            style={unifiedControlButton}
                            onClick={() => {
                                // Reset all reaction colors
                                Object.keys(reactionColors).forEach(reactionId => {
                                    setReactionColor(reactionId, '');
                                });
                                // Reset all species colors
                                availableSpecies.forEach(species => {
                                    resetSpeciesToElementColors(species.id);
                                });
                                // Reset all element colors
                                Object.keys(elementColors).forEach(element => {
                                    setElementColor(element, '');
                                });
                                // Clear all focus
                                clearAllFocus();
                            }}
                        >
                            🔄 Reset Colors
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

// Style for count input
const countInputStyle: React.CSSProperties = {
    width: '60px',
    padding: '4px 6px',
    border: '1px solid #555',
    borderRadius: '4px',
    backgroundColor: 'rgba(40, 40, 40, 0.9)',
    color: '#fff',
    fontSize: '12px',
    textAlign: 'center',
    marginRight: '8px',
};

UnifiedSpeciesPanel.displayName = 'UnifiedSpeciesPanel';

export default UnifiedSpeciesPanel;

